import { apiDelete, apiGet, apiPost, ApiResponse } from "@miragon/react-utils-api";

export interface CreateExecutionCommand {
    name: string;
    rampUpPeriodMs: number;
    scenarioId: string;
    variables: {
        [key: string]: string;
    };
}

export interface CreateRoleCommand {
    description: string;
    group: string;
    name: string;
    script: string;
    variables: CreateRoleCommandVariable[];
}

export interface CreateRoleCommandVariable {
    defaultValue?: string;
    name: string;
    required: boolean;
    type: VariableType;
}

export interface CreateScenarioCommand {
    description: string;
    group: string;
    name: string;
    roles: CreateScenarioCommandRole[];
    variables: CreateScenarioCommandVariable[];
}

export interface CreateScenarioCommandRole {
    instances: CreateScenarioCommandRoleInstance[];
    name: string;
    roleId: string;
}

export interface CreateScenarioCommandRoleInstance {
    name: string;
    variables: {
        [key: string]: string;
    };
}

export interface CreateScenarioCommandVariable {
    defaultValue?: string;
    name: string;
    required: boolean;
    type: VariableType;
}

export interface DeletedItem {
    id: string;
    version: number;
}

export interface DisableExecutionRoleCommand {
    executionId: string;
    executionRoleIds: string[];
}

export interface EnableExecutionRoleCommand {
    executionId: string;
    executionRoleIds: string[];
}

export interface ExecutionErrorRO {
    error: string;
    executionId: string;
    id: string;
    roleId: string;
    roleVersion: string;
    script: string;
    stacktrace?: string;
    timestamp: string;
    variables: {
        [key: string]: string;
    };
    version: number;
    workerId: string;
}

export interface ExecutionErrorsDeletedStreamingEvent {
    data: DeletedItem[];
    eventName: "EXECUTION_ERRORS_DELETED";
}

export interface ExecutionErrorsUpdatedStreamingEvent {
    data: ExecutionErrorRO[];
    eventName: "EXECUTION_ERRORS_UPDATED";
}

export interface ExecutionRO {
    created: string;
    finished?: string;
    id: string;
    name: string;
    rampUpPeriodMs: number;
    roles: ExecutionRoleRO[];
    scenarioId: string;
    started?: string;
    state: ExecutionState;
    variables: {
        [key: string]: string;
    };
    version: number;
}

export interface ExecutionRoleRO {
    disabled: boolean;
    id: string;
    roleId: string;
    roleVersion: number;
    script: string;
    variables: {
        [key: string]: string;
    };
    version: number;
}

export interface ExecutionsDeletedStreamingEvent {
    data: DeletedItem[];
    eventName: "EXECUTIONS_DELETED";
}

export type ExecutionState = "CREATED" | "STARTED" | "FINISHED";

export interface ExecutionsUpdatedStreamingEvent {
    data: ExecutionRO[];
    eventName: "EXECUTIONS_UPDATED";
}

export interface FinishExecutionCommand {
    executionId: string;
}

export interface GetWorkerLogsUrlCommand {
    workerId: string;
}

export interface RoleRO {
    description: string;
    group: string;
    id: string;
    name: string;
    script: string;
    variables: RoleVariableRO[];
    version: number;
}

export interface RolesUpdatedStreamingEvent {
    data: RoleRO[];
    eventName: "ROLES_UPDATED";
}

export interface RoleVariableRO {
    defaultValue?: string;
    id: string;
    name: string;
    required: boolean;
    type: VariableType;
    version: number;
}

export interface ScenarioRO {
    description: string;
    group: string;
    id: string;
    name: string;
    roles: ScenarioRoleRO[];
    variables: ScenarioVariableRO[];
    version: number;
}

export interface ScenarioRoleInstanceRO {
    id: string;
    name: string;
    variables: {
        [key: string]: string;
    };
    version: number;
}

export interface ScenarioRoleRO {
    id: string;
    instances: ScenarioRoleInstanceRO[];
    name: string;
    roleId: string;
    version: number;
}

export interface ScenariosUpdatedStreamingEvent {
    data: ScenarioRO[];
    eventName: "SCENARIOS_UPDATED";
}

export interface ScenarioVariableRO {
    defaultValue?: string;
    id: string;
    name: string;
    required: boolean;
    type: VariableType;
    version: number;
}

export interface StartExecutionCommand {
    executionId: string;
}

export interface UpdateRoleCommand {
    description: string;
    group: string;
    name: string;
    script: string;
    variables: UpdateRoleCommandVariable[];
}

export interface UpdateRoleCommandVariable {
    defaultValue?: string;
    name: string;
    required: boolean;
    type: VariableType;
}

export interface UpdateScenarioCommand {
    description: string;
    group: string;
    name: string;
    roles: UpdateScenarioCommandRole[];
    variables: UpdateScenarioCommandVariable[];
}

export interface UpdateScenarioCommandRole {
    instances: UpdateScenarioCommandRoleInstance[];
    name: string;
    roleId: string;
}

export interface UpdateScenarioCommandRoleInstance {
    name?: string;
    variables?: {
        [key: string]: string;
    };
}

export interface UpdateScenarioCommandVariable {
    defaultValue?: string;
    name: string;
    required: boolean;
    type: VariableType;
}

export type VariableType = "STRING" | "NUMBER" | "BOOLEAN" | "SECRET";

export interface WorkerAssignmentRO {
    executionId: string;
    executionRoleId: string;
    roleId: string;
    roleVersion: number;
}

export interface WorkerRO {
    id: string;
    lastHeartbeat: string;
    screenshotRequested: boolean;
    workerAssignment?: WorkerAssignmentRO;
    workerState: WorkerState;
}

export interface WorkersDeletedStreamingEvent {
    data: DeletedItem[];
    eventName: "WORKERS_DELETED";
}

export type WorkerState = "IDLE" | "WORKING";

export interface WorkersUpdatedStreamingEvent {
    data: WorkerRO[];
    eventName: "WORKERS_UPDATED";
}

// noinspection JSUnusedGlobalSymbols
export class StreamingEventTypeAssertions {
    static isExecutionErrorsDeletedStreamingEvent = (
        model: any
    ): model is ExecutionErrorsDeletedStreamingEvent => {
        return model.eventName === "EXECUTION_ERRORS_DELETED";
    };

    static isExecutionErrorsUpdatedStreamingEvent = (
        model: any
    ): model is ExecutionErrorsUpdatedStreamingEvent => {
        return model.eventName === "EXECUTION_ERRORS_UPDATED";
    };

    static isExecutionsDeletedStreamingEvent = (
        model: any
    ): model is ExecutionsDeletedStreamingEvent => {
        return model.eventName === "EXECUTIONS_DELETED";
    };

    static isExecutionsUpdatedStreamingEvent = (
        model: any
    ): model is ExecutionsUpdatedStreamingEvent => {
        return model.eventName === "EXECUTIONS_UPDATED";
    };

    static isRolesUpdatedStreamingEvent = (model: any): model is RolesUpdatedStreamingEvent => {
        return model.eventName === "ROLES_UPDATED";
    };

    static isScenariosUpdatedStreamingEvent = (
        model: any
    ): model is ScenariosUpdatedStreamingEvent => {
        return model.eventName === "SCENARIOS_UPDATED";
    };

    static isWorkersDeletedStreamingEvent = (model: any): model is WorkersDeletedStreamingEvent => {
        return model.eventName === "WORKERS_DELETED";
    };

    static isWorkersUpdatedStreamingEvent = (model: any): model is WorkersUpdatedStreamingEvent => {
        return model.eventName === "WORKERS_UPDATED";
    };
}

export type StreamingEvent =
    | ExecutionErrorsDeletedStreamingEvent
    | ExecutionErrorsUpdatedStreamingEvent
    | ExecutionsDeletedStreamingEvent
    | ExecutionsUpdatedStreamingEvent
    | RolesUpdatedStreamingEvent
    | ScenariosUpdatedStreamingEvent
    | WorkersDeletedStreamingEvent
    | WorkersUpdatedStreamingEvent;

// Shortcut for Async Response
type AR<T> = Promise<ApiResponse<T>>;

// noinspection JSUnusedGlobalSymbols
export class ControllerAPI {
    static clearCache = (): AR<void> => {
        return apiPost<void>(`/api/controller/cache/clear`);
    };

    static createExecution = (body: CreateExecutionCommand): AR<ExecutionRO> => {
        return apiPost<ExecutionRO>(`/api/controller/execution`, body);
    };

    static createRole = (body: CreateRoleCommand): AR<RoleRO> => {
        return apiPost<RoleRO>(`/api/controller/role`, body);
    };

    static createScenario = (body: CreateScenarioCommand): AR<ScenarioRO> => {
        return apiPost<ScenarioRO>(`/api/controller/scenario`, body);
    };

    static deleteExecution = (id: string): AR<void> => {
        return apiDelete<void>(`/api/controller/execution/${id}`);
    };

    static deleteRole = (roleId: string): AR<void> => {
        return apiDelete<void>(`/api/controller/role/${roleId}`);
    };

    static deleteScenario = (scenarioId: string): AR<void> => {
        return apiDelete<void>(`/api/controller/scenario/${scenarioId}`);
    };

    static disableExecutionRoles = (body: DisableExecutionRoleCommand): AR<ExecutionRO> => {
        return apiPost<ExecutionRO>(`/api/controller/execution/role/disable`, body);
    };

    static enableExecutionRoles = (body: EnableExecutionRoleCommand): AR<ExecutionRO> => {
        return apiPost<ExecutionRO>(`/api/controller/execution/role/enable`, body);
    };

    static finishExecution = (body: FinishExecutionCommand): AR<ExecutionRO> => {
        return apiPost<ExecutionRO>(`/api/controller/execution/start`, body);
    };

    static getWorkerLogsUrl = (body: GetWorkerLogsUrlCommand): AR<string> => {
        return apiPost<string>(`/api/controller/worker/logs`, body);
    };

    static loadExecutionErrors = (): AR<ExecutionErrorRO[]> => {
        return apiGet<ExecutionErrorRO[]>(`/api/controller/execution/error`);
    };

    static loadExecutions = (): AR<ExecutionRO[]> => {
        return apiGet<ExecutionRO[]>(`/api/controller/execution`);
    };

    static loadRoles = (): AR<RoleRO[]> => {
        return apiGet<RoleRO[]>(`/api/controller/role`);
    };

    static loadScenarios = (): AR<ScenarioRO[]> => {
        return apiGet<ScenarioRO[]>(`/api/controller/scenario`);
    };

    static loadWorkers = (): AR<WorkerRO[]> => {
        return apiGet<WorkerRO[]>(`/api/controller/worker`);
    };

    static loadWorkerScreenshot = (id: string): AR<string> => {
        return apiGet<string>(`/api/controller/worker/${id}/screenshot`);
    };

    static requestWorkerScreenshot = (id: string): AR<void> => {
        return apiPost<void>(`/api/controller/worker/${id}/screenshot`);
    };

    static startExecution = (body: StartExecutionCommand): AR<ExecutionRO> => {
        return apiPost<ExecutionRO>(`/api/controller/execution/finish`, body);
    };

    static updateRole = (roleId: string, body: UpdateRoleCommand): AR<RoleRO> => {
        return apiPost<RoleRO>(`/api/controller/role/${roleId}`, body);
    };

    static updateScenario = (scenarioId: string, body: UpdateScenarioCommand): AR<ScenarioRO> => {
        return apiPost<ScenarioRO>(`/api/controller/scenario/${scenarioId}`, body);
    };
}
